import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Star, Clock, Phone, Globe, ExternalLink } from 'lucide-react';

const GoogleBarDetail = () => {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [bar, setBar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBarData();
  }, [placeId]);

  const fetchBarData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/google-places/${placeId}`);
      setBar(response.data);
    } catch (e) {
      console.error('Failed to fetch bar details:', e);
      setError('Failed to load bar details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading bar details...</div>
      </div>
    );
  }

  if (error || !bar) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-white mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="text-center text-white">
          <p className="text-xl">{error || 'Bar not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold text-white">{bar.name}</h1>
        <Badge variant="outline" className="border-gray-500 text-gray-400">
          Non-Partner
        </Badge>
      </div>

      {/* Bar Image */}
      {bar.image_url && (
        <div className="mb-6 rounded-xl overflow-hidden">
          <img 
            src={bar.image_url} 
            alt={bar.name} 
            className="w-full h-64 object-cover"
          />
        </div>
      )}

      {/* Basic Info */}
      <Card className="glass p-6 border border-slate-700 mb-6">
        <div className="space-y-4">
          {/* Rating */}
          {bar.rating > 0 && (
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span className="text-white text-lg font-semibold">{bar.rating}</span>
              <span className="text-slate-400">/ 5</span>
              {bar.price_level && (
                <span className="ml-4 text-green-400">
                  {'$'.repeat(bar.price_level)}
                </span>
              )}
            </div>
          )}

          {/* Address */}
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-purple-400 mt-1" />
            <p className="text-slate-300">{bar.address}</p>
          </div>

          {/* Open Status */}
          {bar.is_open !== undefined && (
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-purple-400" />
              <span className={bar.is_open ? 'text-green-400' : 'text-red-400'}>
                {bar.is_open ? '✅ Open Now' : '❌ Closed'}
              </span>
            </div>
          )}

          {/* Phone */}
          {bar.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-purple-400" />
              <a href={`tel:${bar.phone}`} className="text-slate-300 hover:text-purple-400">
                {bar.phone}
              </a>
            </div>
          )}

          {/* Website */}
          {bar.website && (
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-purple-400" />
              <a 
                href={bar.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                Visit Website <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </Card>

      {/* Opening Hours */}
      {bar.opening_hours && bar.opening_hours.length > 0 && (
        <Card className="glass p-6 border border-slate-700 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-400" />
            Opening Hours
          </h3>
          <div className="space-y-2">
            {bar.opening_hours.map((hours, index) => (
              <p key={index} className="text-slate-300 text-sm">{hours}</p>
            ))}
          </div>
        </Card>
      )}

      {/* Google Reviews */}
      {bar.reviews && bar.reviews.length > 0 && (
        <Card className="glass p-6 border border-slate-700 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Google Reviews
          </h3>
          <div className="space-y-4">
            {bar.reviews.map((review, index) => (
              <div key={index} className="border-b border-slate-700 pb-4 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white font-medium">{review.author}</span>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-slate-400 text-sm ml-1">{review.rating}</span>
                  </div>
                  <span className="text-slate-500 text-xs">{review.time}</span>
                </div>
                <p className="text-slate-300 text-sm">{review.text}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* CTA */}
      <Card className="glass p-6 border border-purple-500/50 text-center">
        <p className="text-slate-300 mb-4">
          This bar isn't a NightBuZz partner yet. Partner bars offer check-ins, live stats, discounts, and more!
        </p>
        <p className="text-purple-400 text-sm">
          Know the owner? Tell them about NightBuZz! 🍻
        </p>
      </Card>
    </div>
  );
};

export default GoogleBarDetail;
